import { ReactElement } from 'react';

import { Timestamp } from 'firebase/firestore';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import AcUnitIcon from '@mui/icons-material/AcUnit';
import CelebrationIcon from '@mui/icons-material/Celebration';
import CloudIcon from '@mui/icons-material/Cloud';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import FilterDramaIcon from '@mui/icons-material/FilterDrama';
import GolfCourseIcon from '@mui/icons-material/GolfCourse';
import GrainIcon from '@mui/icons-material/Grain';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MoneyIcon from '@mui/icons-material/Money';
import OpacityIcon from '@mui/icons-material/Opacity';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TagIcon from '@mui/icons-material/Tag';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WbSunnyIcon from '@mui/icons-material/WbSunny';

import { AirQualityForecast } from '../../types/AirQualityForecast';
import Event, { EVENT_COST_PAID_BY, EVENT_REG_TYPES, PARTICIPANT_ORDERS } from '../../types/Event';
import { WeatherForecast, WEATHER_CONDITIONS } from '../../types/WeatherForecast';
import { getContrastingColor } from '../../utils/colorUtils';
import {
  CurrentDateTime,
  DisplayDate,
  DisplayDateTime,
  DisplayTime,
  IsEndOfDay,
  IsEventOver,
  IsStartOfDay,
  IsWithin24Hours,
  ToDayJS,
} from '../../utils/utils';
import { GratiCardDetail } from '../common/GratiCard';

/**
 * Get league name with optional season
 */
export function getEventLeagueName(event: Event): string {
  return `${event.leagueName ? `${event.leagueName}${event.seasonName ? ` (${event.seasonName})` : ''}` : ''}`;
}

/**
 * Get category text (org - league (season))
 */
export function getEventCategory(event: Event): string {
  if (!(event.name?.trim())) {
    return event.orgName ?? '';
  } else {
    return `${event.orgName ?? ''} - ${getEventLeagueName(event)}`;
  }
}

/**
 * Get title (event name or league name fallback)
 */
export function getEventTitle(event: Event): string {
  if (event) {
    if (event.name?.trim()) {
      return event.name.trim();
    } else {
      return getEventLeagueName(event);
    }
  } else {
    return '';
  }
}

/**
 * Get signup status text
 */
export function getEventStatusText(event: Event): string {
  const currentDateTime = CurrentDateTime();
  if (event) {
    const signupOpensAt = event.signupOpensAt as Timestamp;
    const signupClosesAt = event.signupClosesAt as Timestamp;

    // Check if signup is open
    if (currentDateTime > signupOpensAt && currentDateTime < signupClosesAt) {
      const isEndOfDay = IsEndOfDay(signupClosesAt, event.timezone);
      const isWithin24Hours = IsWithin24Hours(signupClosesAt);

      if (!isWithin24Hours || isEndOfDay) {
        return `Signup by ${DisplayDate(
          signupClosesAt.toDate(),
          event.timezone,
          'MMM D'
        )}`;
      } else {
        return `Signup by ${DisplayDateTime(signupClosesAt, event.timezone)}`;
      }
    } else if (currentDateTime < signupOpensAt) {
      const isStartOfDay = IsStartOfDay(signupOpensAt, event.timezone);
      const isWithin24Hours = IsWithin24Hours(signupOpensAt);

      if (!isWithin24Hours || isStartOfDay) {
        return `Signup opens ${DisplayDate(
          signupOpensAt.toDate(),
          event.timezone,
          'MMM D'
        )}`;
      } else {
        return `Signup opens ${DisplayDateTime(signupOpensAt, event.timezone)}`;
      }
    } else if (IsEventOver(event)) {
      return 'Event has passed';
    } else if (currentDateTime > event.days[0].datetime) {
      return 'Happening now';
    } else {
      return 'Signup is closed.';
    }
  } else {
    return '';
  }
}

/**
 * Get registration stats (X/Y spots filled)
 */
export function getEventStats(event: Event, teamSignedUp?: number | null, teamName?: string | null): string {
  const hasNoLimit = event.maxPlayers === -1 || event.maxPlayers === 0 || !event.maxPlayers;
  const teamSize = event.teamStructure?.size;
  const perTeamSuffix = teamSize
    ? teamSignedUp != null
      ? ` · ${teamSignedUp} of ${teamSize} for ${teamName ?? 'your team'}`
      : ` · ${teamSize} per team`
    : '';
  switch (event.registrationType) {
    case EVENT_REG_TYPES.EXTERNAL:
      return (
        hasNoLimit ? 'Registration via mail.' : `Maximum ${event.maxPlayers} players (Registration via mail)`
      );
    default: {
      const count = event.registrationCount ?? 0;
      const max = event.maxPlayers;
      return max > 0
        ? `${count}/${max} spots filled.${perTeamSuffix}`
        : `${count} signed up${perTeamSuffix}`;
    }
  }
}

/**
 * Get time detail for GratiCard
 */
export function getEventTimeDetail(event: Event): GratiCardDetail | undefined {
  if (!event || !event.days || event.days.length === 0) return undefined;

  const firstDay = event.days[0];
  const isMultiDay = event.days.length > 1;

  if (event.isGolf) {
    // Golf event
    if (isMultiDay) {
      const time = DisplayTime(firstDay.datetime as Timestamp, event.timezone);
      return {
        icon: <ScheduleIcon fontSize="small" />,
        preText: firstDay.isShotgun ? 'First round shotgun start at' : 'First round starts at',
        text: time,
        postText: '.'
      };
    } else {
      const time = DisplayTime(firstDay.datetime as Timestamp, event.timezone);
      return {
        icon: <ScheduleIcon fontSize="small" />,
        preText: firstDay.isShotgun ? 'Shotgun start at' : 'Starts at',
        text: time,
        postText: '.'
      };
    }
  } else {
    // Non-golf event
    if (isMultiDay) {
      const time = DisplayTime(firstDay.datetime as Timestamp, event.timezone);
      return {
        icon: <ScheduleIcon fontSize="small" />,
        preText: 'First day starts at',
        text: time,
        postText: '.'
      };
    } else {
      const startTime = ToDayJS(firstDay.datetime as Timestamp, event.timezone);
      const endTime = startTime.add(firstDay.duration || 0, 'minute');
      const timeRange = `${DisplayTime(firstDay.datetime as Timestamp, event.timezone)} - ${DisplayTime(Timestamp.fromDate(endTime.toDate()), event.timezone)}`;
      return {
        icon: <ScheduleIcon fontSize="small" />,
        text: timeRange
      };
    }
  }
}

/**
 * Get location detail for GratiCard
 */
export function getEventLocationDetail(event: Event): GratiCardDetail | undefined {
  if (!event || !event.days || event.days.length === 0) return undefined;

  const firstDay = event.days[0];
  const isMultiDay = event.days.length > 1;
  const allGolf = event.days.every(day => day.isGolf === true);
  const allSocial = event.days.every(day => day.isGolf === false);

  // Determine icon
  const LocationIcon = isMultiDay
    ? (allGolf ? GolfCourseIcon : allSocial ? CelebrationIcon : LocationOnIcon)
    : (firstDay.isGolf ? GolfCourseIcon : CelebrationIcon);

  if (isMultiDay) {
    // Multi-day event
    if (firstDay.isGolf && firstDay.courseId) {
      const course = event.coursePairs?.find(c => c.id === firstDay.courseId);
      const courseName = course?.name || 'Unknown Course';

      return {
        icon: <LocationIcon fontSize="small" />,
        preText: 'Starts at',
        text: courseName,
        postText: '.'
      };
    } else {
      const locationName = firstDay.socialLocation || 'TBD';

      return {
        icon: <LocationIcon fontSize="small" />,
        preText: 'Starts at',
        text: locationName,
        postText: '.'
      };
    }
  } else {
    // Single day event
    if (firstDay.isGolf && firstDay.courseId) {
      const course = event.coursePairs?.find(c => c.id === firstDay.courseId);
      const courseName = course?.name || 'Unknown Course';

      return {
        icon: <LocationIcon fontSize="small" />,
        text: courseName
      };
    } else {
      const locationName = firstDay.socialLocation || 'TBD';

      return {
        icon: <LocationIcon fontSize="small" />,
        text: locationName
      };
    }
  }
}

export function formatWeatherSummary(forecast: WeatherForecast): string {
  const condition = forecast.condition.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
  const temps = `${forecast.tempLowF}° - ${forecast.tempHighF}°`;
  const wind = forecast.windMph > 0 ? `, winds at ${forecast.windMph}mph` : '';
  return `${condition}, ${temps}${wind}`;
}

/**
 * Get event details array for GratiCard
 */
export function getEventDetails(event: Event, onGgidCopy?: () => void, forecast?: WeatherForecast, onEmailCopy?: () => void, aqiForecast?: AirQualityForecast): GratiCardDetail[] {
  if (!event?.days?.length) return [];

  const isMultiDay = event.days.length > 1;

  let details: GratiCardDetail[];

  if (isMultiDay) {
    // Group EventDays by calendar date
    const groupedByDate: Record<string, typeof event.days> = {};
    for (const day of event.days) {
      const dateKey = DisplayDate((day.datetime as Timestamp).toDate(), event.timezone, 'YYYY-MM-DD');
      if (!groupedByDate[dateKey]) groupedByDate[dateKey] = [];
      groupedByDate[dateKey].push(day);
    }

    const sortedDates = Object.keys(groupedByDate).sort();
    let roundNumber = 0;

    details = sortedDates.map((dateKey) => {
      const daysOnDate = groupedByDate[dateKey].slice().sort(
        (a, b) => (a.datetime as Timestamp).toMillis() - (b.datetime as Timestamp).toMillis()
      );
      const earliest = daysOnDate[0];
      // A social day isn't a "round" at all — it never counts toward Day N and is never
      // Practice (that concept is golf-only), so it gets its own label instead.
      const daySocial = daysOnDate.every(d => d.isGolf === false);
      const allPractice = !daySocial && daysOnDate.every(d => d.isPractice);

      let dayLabel: string;
      if (daySocial) {
        dayLabel = earliest.socialName?.trim() || 'Social';
      } else {
        if (!allPractice) roundNumber++;
        dayLabel = allPractice ? 'Practice' : `Day ${roundNumber}`;
      }

      const date = DisplayDate((earliest.datetime as Timestamp).toDate(), event.timezone, 'MMM D');
      const time = DisplayTime(earliest.datetime as Timestamp, event.timezone);

      let locationName = '';
      if (earliest.isGolf && earliest.courseId) {
        const course = event.coursePairs?.find(c => c.id === earliest.courseId);
        locationName = course?.shortName || course?.name || '';
      } else if (earliest.socialLocation) {
        locationName = earliest.socialLocation;
      }

      const timeText = !daySocial && earliest.isShotgun ? `${date} ${time} shotgun` : `${date} ${time}`;
      const postText = locationName ? ` at ${locationName}.` : '.';

      return {
        icon: daySocial ? <CelebrationIcon fontSize="small" /> : <GolfCourseIcon fontSize="small" />,
        preText: `${dayLabel}:`,
        text: timeText,
        postText,
      };
    });
  } else {
    details = [];
    const timeDetail = getEventTimeDetail(event);
    const locationDetail = getEventLocationDetail(event);
    if (timeDetail) details.push(timeDetail);
    if (locationDetail) details.push(locationDetail);
  }

  if (forecast) {
    details.push({
      icon: <WeatherConditionIcon condition={forecast.condition} />,
      text: formatWeatherSummary(forecast),
      ...(aqiForecast && { badge: <AirQualityBadge forecast={aqiForecast} /> }),
    });
  }

  if (event.ggid) details.push({ icon: <TagIcon fontSize="small" />, preText: 'GGID:', text: event.ggid, actionIcon: <ContentCopyIcon fontSize="small" />, actionHandler: onGgidCopy });
  if (event.cost) {
    const costText =
      event.costChargedBy === EVENT_COST_PAID_BY.ACCOUNT
        ? event.costChargedOn instanceof Timestamp
          ? `${event.cost} charged to your account on ${DisplayDate(event.costChargedOn.toDate(), event.timezone, 'MM/DD/YY')}.`
          : `${event.cost} charged to your account.`
        : event.costChargedBy === EVENT_COST_PAID_BY.CHECK
        ? `${event.cost} paid via check.`
        : event.cost;
    details.push({ icon: <MoneyIcon fontSize="small" />, text: costText });
  }

  const participantOrderLabels: Partial<Record<string, string>> = {
    [PARTICIPANT_ORDERS.GHIN_INDEX]: 'index',
    [PARTICIPANT_ORDERS.DRAWING]: 'drawing',
    [PARTICIPANT_ORDERS.MGR_CHOICE]: 'manager',
  };
  const orderLabel = event.participantOrder ? participantOrderLabels[event.participantOrder] : undefined;
  if (orderLabel) details.push({ icon: <HowToRegIcon fontSize="small" />, preText: 'Signup determined by', text: orderLabel, postText: '.' });

  const now = new Date();
  const allDate = event.showRosterToAllDate instanceof Timestamp ? event.showRosterToAllDate.toDate() : null;
  const teamDate = event.showRosterToTeamDate instanceof Timestamp ? event.showRosterToTeamDate.toDate() : null;
  const allFuture = allDate && allDate > now;
  const teamFuture = teamDate && teamDate > now;
  if (allFuture || teamFuture) {
    const allStr = allFuture ? DisplayDate(allDate, event.timezone, 'MMM D') : null;
    const teamStr = teamFuture ? DisplayDate(teamDate!, event.timezone, 'MMM D') : null;
    if (allStr && teamStr && allStr === teamStr) {
      details.push({ icon: <VisibilityOffIcon fontSize="small" />, preText: 'Rosters visible on', text: allStr, postText: '.' });
    } else {
      if (allStr) details.push({ icon: <VisibilityOffIcon fontSize="small" />, preText: 'Event roster visible on', text: allStr, postText: '.' });
      if (teamStr) details.push({ icon: <VisibilityOffIcon fontSize="small" />, preText: 'Team roster visible to members on', text: teamStr, postText: '.' });
    }
  }

  if (event.registrationEmail && event.registrationType !== EVENT_REG_TYPES.EMAIL) {
    details.push({
      icon: <EmailIcon fontSize="small" />,
      preText: 'Questions? Contact',
      text: event.registrationEmail,
      actionIcon: <ContentCopyIcon fontSize="small" />,
      actionHandler: onEmailCopy,
    });
  }

  return details;
}

/**
 * Compact single-line collapsed summary for MD3 expansion panel pattern.
 * Shows [date] · [name] with a signup status chip. GratiCard renders the expand affordance below.
 */
export function getEventCollapsedSummary(event: Event): ReactElement {
  const currentDateTime = CurrentDateTime();
  const signupOpensAt = event.signupOpensAt as Timestamp;
  const signupClosesAt = event.signupClosesAt as Timestamp;
  const hasPassed = event.days?.length
    ? currentDateTime > (event.days[event.days.length - 1].datetime as Timestamp)
    : false;

  let statusLabel: string | null = null;
  let statusColor: 'success' | 'default' = 'default';
  if (!hasPassed) {
    if (currentDateTime > signupOpensAt && currentDateTime < signupClosesAt) {
      statusLabel = 'Open';
      statusColor = 'success';
    } else if (currentDateTime < signupOpensAt) {
      statusLabel = 'Soon';
    } else {
      statusLabel = 'Closed';
    }
  }

  let dateText = '';
  if (event.days?.length) {
    const firstDate = DisplayDate((event.days[0].datetime as Timestamp).toDate(), event.timezone, 'MMM D');
    if (event.days.length > 1) {
      const lastDay = event.days[event.days.length - 1];
      const lastDate = DisplayDate((lastDay.datetime as Timestamp).toDate(), event.timezone, 'MMM D');
      dateText = firstDate === lastDate ? firstDate : `${firstDate}–${lastDate}`;
    } else {
      dateText = firstDate;
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        pl: 1.25,
        pr: 0.75,
        minHeight: '3rem',
        overflow: 'hidden',
        borderLeft: `3px solid ${event.color ?? 'transparent'}`,
      }}
    >
      {dateText && (
        <Typography variant="body2" sx={{ flexShrink: 0, color: 'text.secondary' }}>
          {dateText}
        </Typography>
      )}
      {statusLabel && (
        <>
          {dateText && <Typography variant="body2" sx={{ flexShrink: 0, color: 'text.disabled' }}>·</Typography>}
          <Typography
            variant="body2"
            sx={{
              flexShrink: 0,
              fontWeight: 500,
              color: statusColor === 'success' ? 'success.main' : 'text.disabled',
            }}
          >
            {statusLabel}
          </Typography>
        </>
      )}
    </Box>
  );
}

function toHex(value: number): string {
  return Math.round(value * 255).toString(16).padStart(2, '0');
}

function AirQualityBadge({ forecast }: { forecast: AirQualityForecast }): ReactElement {
  const { red, green, blue } = forecast.color;
  const bg = `#${toHex(red)}${toHex(green)}${toHex(blue)}`;
  return (
    <Chip
      size="small"
      label={`AQI ${forecast.aqi} · ${forecast.category}`}
      sx={{ backgroundColor: bg, color: getContrastingColor(bg) }}
    />
  );
}

function WeatherConditionIcon({ condition, sx }: { condition: string; sx?: object }): ReactElement {
  const props = { sx: { fontSize: '1.1rem', ...sx } };
  switch (condition) {
    case WEATHER_CONDITIONS.SUNNY:       return <WbSunnyIcon {...props} />;
    case WEATHER_CONDITIONS.PARTLY_CLOUDY: return <FilterDramaIcon {...props} />;
    case WEATHER_CONDITIONS.CLOUDY:
    case WEATHER_CONDITIONS.OVERCAST:
    case WEATHER_CONDITIONS.FOG:        return <CloudIcon {...props} />;
    case WEATHER_CONDITIONS.DRIZZLE:    return <GrainIcon {...props} />;
    case WEATHER_CONDITIONS.RAIN:       return <OpacityIcon {...props} />;
    case WEATHER_CONDITIONS.THUNDERSTORM: return <ThunderstormIcon {...props} />;
    case WEATHER_CONDITIONS.SNOW:
    case WEATHER_CONDITIONS.SLEET:      return <AcUnitIcon {...props} />;
    default:                            return <CloudIcon {...props} />;
  }
}

export function EventSpotlight({ event }: { event: Event }): ReactElement {
  if (!event.days || event.days.length === 0) {
    return <Box />;
  }

  const eventDate = (event.days[0].datetime as Timestamp).toDate();
  const monthText = eventDate.toLocaleString('default', { month: 'short', timeZone: event.timezone });
  const dayText = eventDate.toLocaleString('default', { day: 'numeric', timeZone: event.timezone });

  return (
    <Box>
      <Stack direction="column" sx={{ alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 400, lineHeight: 'normal', textTransform: 'uppercase' }}>
          {monthText}
        </Typography>
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 'normal', textTransform: 'uppercase' }}>
          {dayText}
        </Typography>
      </Stack>
    </Box>
  );
}
